import { useCallback, useMemo, useRef, useState } from 'react';
import { invoiceApi } from '../api/invoiceApi';
import type { InvoiceQueryParams, TransformedInvoice } from '../types';

export type SelectionMode = 'specific' | 'all';

export interface JuniperSelection {
  mode: SelectionMode;
  /** Count of selected items the UI should display. */
  selectedCount: number;
  /** Total matching records for the current filters (derived from page response). */
  totalMatching: number;
  /** True when selection count exceeds the currently-rendered visible rows. */
  hasCrossPageSelection: boolean;
  /** IDs the user has explicitly excluded while in 'all' mode. */
  excludedIds: Set<string>;

  /** Is a given transaction considered selected right now? */
  isSelected: (id: string) => boolean;
  /** Toggle selection for a single row (from its checkbox). */
  toggle: (invoice: TransformedInvoice) => void;
  /** Header checkbox — toggles visible rows on/off (or flips to 'specific' mode). */
  toggleVisible: (visible: TransformedInvoice[]) => void;
  /** Clear all selection state. */
  clear: () => void;
  /** Flip to 'all' mode: select every record matching the current filters. */
  selectAllMatching: (filterSnapshot: InvoiceQueryParams, totalMatching: number) => void;
  /** Notified when filters change — resets 'all' mode if the snapshot no longer matches. */
  onFiltersChanged: (currentFilters: InvoiceQueryParams) => void;

  /**
   * Resolve the full list of selected juniperIds for a bulk action.
   * In 'specific' mode this is just the explicit ids.
   * In 'all' mode this calls the IDs endpoint with the snapshotted filters,
   * then subtracts `excludedIds`.
   */
  resolveSelectedIds: () => Promise<{ ids: string[]; truncated: boolean }>;

  /**
   * Returns the full TransformedInvoice objects for the explicitly-selected set.
   * Empty in 'all' mode (callers should fall through to resolveSelectedIds).
   */
  getSpecificObjects: () => TransformedInvoice[];
}

const FILTER_KEYS: Array<keyof InvoiceQueryParams> = [
  'dateFrom', 'dateTo', 'dateField', 'uniqueByMatter', 'createdByUserIds',
  'matterId', 'matterType', 'mainBuckets', 'operationalStates', 'trustRequestStates',
  'juniperStatuses', 'pastDue', 'noPaymentInDays', 'customFieldFilters', 'billNumber',
  'searchQuery', 'statusFilter',
];

function filterSignature(params: InvoiceQueryParams): string {
  const subset: Record<string, unknown> = {};
  for (const key of FILTER_KEYS) {
    const value = params[key];
    if (value === undefined || value === null) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    subset[key] = value;
  }
  return JSON.stringify(subset);
}

export function useJuniperSelection(currentTotalMatching: number): JuniperSelection {
  const [mode, setMode] = useState<SelectionMode>('specific');
  const [specific, setSpecific] = useState<Map<string, TransformedInvoice>>(new Map());
  const [excludedIds, setExcludedIds] = useState<Set<string>>(new Set());
  const filterSnapshotRef = useRef<InvoiceQueryParams | null>(null);
  const filterSignatureRef = useRef<string | null>(null);
  const allModeTotalRef = useRef<number>(0);

  const clear = useCallback(() => {
    setMode('specific');
    setSpecific(new Map());
    setExcludedIds(new Set());
    filterSnapshotRef.current = null;
    filterSignatureRef.current = null;
    allModeTotalRef.current = 0;
  }, []);

  const isSelected = useCallback((id: string) => {
    if (mode === 'all') return !excludedIds.has(id);
    return specific.has(id);
  }, [mode, specific, excludedIds]);

  const toggle = useCallback((invoice: TransformedInvoice) => {
    const id = invoice.id;
    if (mode === 'all') {
      setExcludedIds(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    } else {
      setSpecific(prev => {
        const next = new Map(prev);
        if (next.has(id)) next.delete(id);
        else next.set(id, invoice);
        return next;
      });
    }
  }, [mode]);

  const toggleVisible = useCallback((visible: TransformedInvoice[]) => {
    if (visible.length === 0) return;
    if (mode === 'all') {
      // In 'all' mode, header toggle = clear selection entirely.
      clear();
      return;
    }
    const allVisibleSelected = visible.every(inv => specific.has(inv.id));
    setSpecific(prev => {
      const next = new Map(prev);
      if (allVisibleSelected) {
        for (const inv of visible) next.delete(inv.id);
      } else {
        for (const inv of visible) next.set(inv.id, inv);
      }
      return next;
    });
  }, [mode, specific, clear]);

  const selectAllMatching = useCallback((filterSnapshot: InvoiceQueryParams, totalMatching: number) => {
    setMode('all');
    setExcludedIds(new Set());
    setSpecific(new Map());
    filterSnapshotRef.current = filterSnapshot;
    filterSignatureRef.current = filterSignature(filterSnapshot);
    allModeTotalRef.current = totalMatching;
  }, []);

  const onFiltersChanged = useCallback((currentFilters: InvoiceQueryParams) => {
    // Reset 'all' mode the moment filters diverge from the snapshot.
    if (mode !== 'all' || !filterSignatureRef.current) return;
    if (filterSignature(currentFilters) !== filterSignatureRef.current) {
      clear();
    }
  }, [mode, clear]);

  const resolveSelectedIds = useCallback(async (): Promise<{ ids: string[]; truncated: boolean }> => {
    if (mode === 'specific') {
      return { ids: Array.from(specific.keys()), truncated: false };
    }
    // 'all' mode — fetch every matching ID from the backend using the snapshotted filters.
    if (!filterSnapshotRef.current) {
      return { ids: [], truncated: false };
    }
    const response = await invoiceApi.getInvoiceIds(filterSnapshotRef.current);
    const filtered = response.ids.filter(id => !excludedIds.has(id));
    return { ids: filtered, truncated: response.truncated };
  }, [mode, specific, excludedIds]);

  const getSpecificObjects = useCallback((): TransformedInvoice[] => {
    if (mode === 'all') return [];
    return Array.from(specific.values());
  }, [mode, specific]);

  const selectedCount = useMemo(() => {
    if (mode === 'all') {
      return Math.max(0, allModeTotalRef.current - excludedIds.size);
    }
    return specific.size;
  }, [mode, specific, excludedIds]);

  const hasCrossPageSelection = useMemo(() => {
    if (mode === 'all') return allModeTotalRef.current > 0;
    return specific.size > 0;
  }, [mode, specific]);

  return {
    mode,
    selectedCount,
    totalMatching: currentTotalMatching,
    hasCrossPageSelection,
    excludedIds,
    isSelected,
    toggle,
    toggleVisible,
    clear,
    selectAllMatching,
    onFiltersChanged,
    resolveSelectedIds,
    getSpecificObjects,
  };
}
